import { inject, Injectable } from '@angular/core';
import {
  AMQPExchange,
  AMQPMessage,
  AMQPSession,
  AMQPSubscription,
  builtinParsers,
  BuiltinParsers,
  JsonSerializable,
} from '@cloudamqp/amqp-client';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { WebsocketMessage } from 'src/app/models/websocket';
import { reportWebSocketError } from 'src/app/reducers/user/user.actions';
import { getDeviceId } from 'src/app/shared/utils.functions';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
type JsonSession = AMQPSession<BuiltinParsers, {}, 'application/json'>;
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
type JsonExchange = AMQPExchange<BuiltinParsers, {}, 'application/json'>;

interface QueuedMessage {
  message: Omit<WebsocketMessage, 'deviceId'>;
  timestamp: number;
  retries: number;
}

@Injectable({ providedIn: 'root' })
export class WebsocketService {
  private readonly exchange = 'amq.topic';
  private readonly maxRetryAttempts = 3;
  private readonly reconnectIntervalMs = 1000;
  private readonly maxReconnectIntervalMs = 30000;
  private readonly heartbeatSeconds = 30;
  private readonly frameMax = 8192;
  // Roughly the time the old implementation needed to exhaust its 3 retry
  // attempts before reporting the outage to the store
  private readonly outageReportDelayMs = 15000;
  private readonly store = inject(Store);
  private readonly url = `wss://${encodeURIComponent('kds_service')}:${encodeURIComponent('8X3Jkx-y=#,27yR')}@buzzing-black-squirrel.rmq4.cloudamqp.com/ws/amqp`;
  private readonly storageKey = 'chef-websocket-message-queue';
  private readonly maxQueueSize = 100;

  public messagesSubject$ = new Subject<WebsocketMessage>();

  private deviceId = '';
  private queue = '';
  private session?: JsonSession;
  private topicExchange?: JsonExchange;
  private subscription?: AMQPSubscription;
  private connectionPromise?: Promise<void>;
  private shouldMaintainConnection = false;
  private outageReported = false;
  private outageReportTimer?: ReturnType<typeof setTimeout>;
  private messageQueue: QueuedMessage[] = [];
  private isProcessingQueue = false;
  private readonly beforeUnloadHandler = () => this.disconnect();

  constructor() {
    // Clean disconnect on page unload to prevent "connection not cleanly closed" errors
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  public async connect(queue: string): Promise<void> {
    if (this.connectionPromise !== undefined) {
      return await this.connectionPromise;
    }

    this.disconnect();
    this.deviceId = getDeviceId();
    if (queue) this.queue = queue;

    // Load any persisted messages from previous sessions
    this.loadQueueFromStorage();

    this.shouldMaintainConnection = true;
    this.connectionPromise = this.startSession().finally(() => {
      this.connectionPromise = undefined;
    });

    return this.connectionPromise;
  }

  public disconnect(): void {
    this.shouldMaintainConnection = false;
    this.clearOutageReportTimer();

    const session = this.session;
    this.session = undefined;
    this.topicExchange = undefined;
    this.subscription = undefined;
    this.connectionPromise = undefined;
    this.outageReported = false;

    // stop() cancels the session's reconnect loop, closes the consumer and the connection
    session?.stop('client disconnect').catch((err) => {
      console.warn('Error closing AMQP session:', err);
    });

    // Save queue to storage before disconnecting
    this.saveQueueToStorage();
  }

  public sendMessage(msg: Omit<WebsocketMessage, 'deviceId'>): void {
    const queuedMessage: QueuedMessage = {
      message: msg,
      timestamp: Date.now(),
      retries: 0,
    };

    if (!this.isConnectionHealthy()) {
      // The session reconnects on its own; the queue is flushed in onconnect
      console.warn('Connection unhealthy, queueing message');
      this.addToQueue(queuedMessage);
      return;
    }

    this.publish(msg).catch((err: unknown) => {
      console.error('Failed to send message, adding to queue:', err);
      this.addToQueue(queuedMessage);
    });
  }

  private async startSession(): Promise<void> {
    // The session reconnects automatically once established, but the initial
    // connection attempt is not retried by the library - do that here
    let attempt = 0;
    while (this.shouldMaintainConnection) {
      try {
        const session = await AMQPSession.connect(this.url, {
          vhost: 'websockets',
          name: 'menutech-service-webclient',
          heartbeat: this.heartbeatSeconds,
          frameMax: this.frameMax,
          reconnectInterval: this.reconnectIntervalMs,
          maxReconnectInterval: this.maxReconnectIntervalMs,
          maxRetries: 0, // reconnect indefinitely
          parsers: builtinParsers,
          defaultContentType: 'application/json',
          onconnect: (s) => this.handleConnected(s),
          ondisconnect: (err) => this.handleDisconnected(err),
        });
        if (!this.shouldMaintainConnection) {
          void session.stop('client disconnect');
        }
        return;
      } catch (err) {
        console.error('Connection error:', err);
        if (attempt + 1 >= this.maxRetryAttempts) this.reportOutage();
        const delay = Math.min(
          this.reconnectIntervalMs * Math.pow(2, attempt),
          this.maxReconnectIntervalMs,
        );
        console.log(`Reconnecting in ${delay}ms...`);
        await new Promise((resolve) => setTimeout(resolve, delay));
        attempt++;
      }
    }
  }

  // Fires on the initial connection and again after every automatic reconnect
  private handleConnected(session: JsonSession): void {
    console.log('WebSocket connected successfully');
    this.session = session;
    this.outageReported = false;
    this.clearOutageReportTimer();
    void this.setupTopology(session);
  }

  private handleDisconnected(error?: Error): void {
    if (!this.shouldMaintainConnection) return;
    console.warn(
      'WebSocket disconnected, session will reconnect automatically',
      error ?? '',
    );
    this.startOutageReportTimer();
  }

  private async setupTopology(session: JsonSession): Promise<void> {
    try {
      this.topicExchange = await session.exchange(this.exchange, 'topic', {
        passive: true,
      });

      // Server-named queues are not auto-recovered, so declare, bind and
      // subscribe on every (re)connect; messages are acked automatically
      // after the callback returns and nacked when it throws
      const q = await session.queue('');
      await q.bind(this.topicExchange, this.queue);
      this.subscription = await q.subscribe({ prefetch: 1 }, (msg) => {
        this.handleIncomingMessage(msg);
      });

      // Process any queued messages
      void this.processMessageQueue();
    } catch (err) {
      console.error('Failed to set up AMQP topology:', err);
    }
  }

  private handleIncomingMessage(msg: AMQPMessage<BuiltinParsers>): void {
    // The session decodes the body via its JSON parser (all peers publish
    // with content-type application/json); fall back to raw bytes defensively
    const body: unknown =
      msg.body instanceof Uint8Array
        ? JSON.parse(msg.bodyToString() ?? 'null')
        : msg.body;
    const parsedMsg = body as WebsocketMessage | null;
    if (parsedMsg && parsedMsg.deviceId !== this.deviceId) {
      this.messagesSubject$.next(parsedMsg);
    }
  }

  private async publish(
    msg: Omit<WebsocketMessage, 'deviceId'>,
  ): Promise<void> {
    if (!this.topicExchange) throw new Error('AMQP exchange is not available');
    // The session serializes the body via its JSON parser and waits for a
    // broker confirm, so a resolved promise means the broker accepted it
    await this.topicExchange.publish(
      { ...msg, deviceId: this.deviceId } as unknown as JsonSerializable,
      {
        routingKey: this.queue,
        expiration: '5000',
        deliveryMode: 1, // transient, matching the pre-session behaviour
      },
    );
  }

  private isConnectionHealthy(): boolean {
    return !!(this.session && !this.session.closed && this.topicExchange);
  }

  private reportOutage(): void {
    if (this.outageReported) return;
    this.outageReported = true;
    this.store.dispatch(reportWebSocketError());
  }

  private startOutageReportTimer(): void {
    if (this.outageReportTimer || this.outageReported) return;
    this.outageReportTimer = setTimeout(() => {
      this.outageReportTimer = undefined;
      if (this.shouldMaintainConnection && !this.isConnectionHealthy()) {
        this.reportOutage();
      }
    }, this.outageReportDelayMs);
  }

  private clearOutageReportTimer(): void {
    if (this.outageReportTimer) {
      clearTimeout(this.outageReportTimer);
      this.outageReportTimer = undefined;
    }
  }

  private addToQueue(queuedMessage: QueuedMessage): void {
    // Check queue size limit
    if (this.messageQueue.length >= this.maxQueueSize) {
      console.warn('Message queue full, removing oldest message');
      this.messageQueue.shift();
    }

    this.messageQueue.push(queuedMessage);
    this.saveQueueToStorage();
  }

  private async processMessageQueue(): Promise<void> {
    if (this.isProcessingQueue || this.messageQueue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;
    console.log(`Processing ${this.messageQueue.length} queued messages`);

    const pending = [...this.messageQueue];
    const failedMessages: QueuedMessage[] = [];

    for (const [index, queuedMsg] of pending.entries()) {
      if (!this.isConnectionHealthy()) {
        // Connection lost while processing, save remaining messages
        failedMessages.push(...pending.slice(index));
        break;
      }

      try {
        // Publishing awaits the broker confirm, which paces the flush
        await this.publish(queuedMsg.message);
      } catch (err) {
        console.error('Failed to send queued message:', err);
        queuedMsg.retries++;

        // Keep message if under retry limit
        if (queuedMsg.retries < this.maxRetryAttempts) {
          failedMessages.push(queuedMsg);
        } else {
          console.error(
            'Message exceeded retry limit, discarding:',
            queuedMsg.message,
          );
        }
      }
    }

    this.messageQueue = failedMessages;
    this.saveQueueToStorage();
    this.isProcessingQueue = false;
  }

  private loadQueueFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as typeof this.messageQueue;
        // Filter out messages older than 24 hours
        const cutoffTime = Date.now() - 24 * 60 * 60 * 1000;
        this.messageQueue = parsed.filter((msg) => msg.timestamp > cutoffTime);

        if (this.messageQueue.length > 0) {
          console.log(
            `Loaded ${this.messageQueue.length} messages from storage`,
          );
        }
      }
    } catch (err) {
      console.error('Failed to load message queue from storage:', err);
      this.messageQueue = [];
    }
  }

  private saveQueueToStorage(): void {
    try {
      if (this.messageQueue.length > 0) {
        localStorage.setItem(
          this.storageKey,
          JSON.stringify(this.messageQueue),
        );
      } else {
        localStorage.removeItem(this.storageKey);
      }
    } catch (err) {
      console.error('Failed to save message queue to storage:', err);
    }
  }
}
